import { useState } from 'react';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import { Field, Form, FormikProvider, useFormik } from 'formik';

import Icons, { getIconByType } from 'shared/Icons';
import Button from 'shared/Button';
import QuestionRecommendations from 'components/QuestionRecommendations';
import CompletionModal from 'popups/PostQuestion/CompletionModal';

import { useClientContext, useTranslationContext, useUserContext } from 'hooks';
import { QuestionServiceInstance, TrackerServiceInstance, UserServiceInstance } from 'services';
import * as interfaces from 'interfaces';

import classes from './PostNewQuestion.module.scss';

export default function PostNewQuestion({ handleClose }: {
  handleClose: () => void;
}) {
  const { client, categories } = useClientContext();
  const { t, translateCustomText, language } = useTranslationContext();
  const { user: currentUser, updateUser, isSignedIn, setIsSignedIn } = useUserContext();

  const [onNewUserFlow, setOnNewUserFlow] = useState(false);
  const [onPromptToEnterData, setOnPromptToEnterData] = useState(false);
  const [questionCharacterCount, setQuestionCharacterCount] = useState(0);

  const [isUserOldVisitor, setIsUserOldVisitor] = useState(false);
  const [showCompletionModule, setShowCompletionModule] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const errorText: { [key: string]: string } = {
    question:  t('Please input your question'),
    category:  t('Please select a category'),
    firstName: t('Please add your name'),
    email:     t('Please enter your email'),
  }

  const validationSchema = Yup.object().shape({
    question: Yup.string().required(errorText.question)
  });

  const userDataValidationSchema = Yup.object().shape({
    email: Yup.string().required(t('Please enter your email')).email(t('Please enter valid email')),
    firstName: onNewUserFlow ? Yup.string().required(t('Please add your name')) : Yup.string(),
    zip: onNewUserFlow ? Yup.string().required(t('Please add zip code')) : Yup.string(),
  });

  const questionFormik = useFormik({
    initialValues: {
      question: '',
      category: ''
    },
    validationSchema,
    onSubmit: (values) => {
      if (isSignedIn) {
        let error: string | null = !values.question ? errorText.question : (
          !values.category ? errorText.category : null
        );

        if (error !== null) {
          toast.error(error);
          return;
        }

        const questionSubmitValues: interfaces.NewQuestionData = {
          userId: currentUser.id,
          clientId: client.id,
          votes: 1,
          text: values.question,
          originalLanguage: language,
          name: userDataFormik.values.firstName,
          category: values.category,
          categoryIcon: getIconByType(values.category),
          asked: true
        };

        QuestionServiceInstance?.createQuestion(questionSubmitValues).then(
          (response: interfaces.QuestionInterface[] | any) => {
            if (response.code) {
              switch (response.code) {
                case "ERROR_QUESTION_DUPLICATED": {
                  toast.error(t("Please make sure your question is original"));
                  break;
                }
                case "ERROR_INAPPROPRIATE_LANGUAGE": {
                  toast.error(t("We've detected inappropriate language in your question. Please rephrase and resubmit"));
                  break;
                }
                case "ERROR_SESSION_DISABLED": {
                  toast.error(t("We are not able to associate this email with your activity"));
                  break;
                }
                default: {
                  toast.error(response.message);
                }
              }
            } else {
              const receivedQuestion = response[0];

              TrackerServiceInstance?.trackEvent('sendQuestion',
                {
                  event_category: 'Questions',
                  questionId: receivedQuestion.id,
                  questionText: receivedQuestion.text,
                  category: receivedQuestion.category
                }
              );

              setShowCompletionModule(true);
            }
          }
        );
      }

    }
  });

  const userDataFormik = useFormik({
    initialValues: {
      email: currentUser.email || '',
      firstName: currentUser.firstName || '',
      lastName: currentUser.lastName || '',
      zip: currentUser.zip || '',
    },
    validationSchema: userDataValidationSchema,
    onSubmit: (values, actions) => {
      if (isSignedIn) {
        questionFormik.handleSubmit();
        return;
      }

      if (!onNewUserFlow) {
        if (values.email) {
          UserServiceInstance?.getUserByEmail(client.id, values.email).then((res: any) => {
            if (res.code === "ERROR_USER_NOT_VERIFIED") {
              toast.error(t("We are not able to associate this email with your activity"));
              return;
            }

            if (!res.data) {
              setOnNewUserFlow(true);
              actions.setTouched({});
            }
            else {
              const response = (res.data || [])[0];

              UserServiceInstance?.storeUserAndSessionData(response.user, response.token);
              TrackerServiceInstance?.setAuthorizationHeader(response.token);

              updateUser({ ...response.user, clientId: client.id });

              if (['user', 'admin'].includes(response.user.accessLevel)) {
                setIsSignedIn(true);
                questionFormik.handleSubmit();
                UserServiceInstance?.sendMagicLink(response.user.email, client.id )
                setMagicLinkSent(true);
              } else {
                setOnNewUserFlow(true);
                setIsUserOldVisitor(true);
              }
            }

          })
        } else
        toast.error(t("Please enter your email"));
      }

      if (onNewUserFlow) {
        if ( values.email && values.firstName && values.zip){
          const payload : {
            email?: string;
            zip: string;
            firstName: string;
            lastName?: string;
          } = {firstName: values.firstName, zip: values.zip};

          if (!isUserOldVisitor)
            payload.email = values.email;

          if (values.lastName)
            payload.lastName = values.lastName;

          UserServiceInstance?.updateCurrentUserSession(payload).then((receivedUser: interfaces.SessionDataInterface) => {
            if (receivedUser.email) {
              UserServiceInstance?.sendMagicLink(receivedUser.email, client.id);

              setMagicLinkSent(true);

              updateUser({...currentUser,
                email: receivedUser.email,
                zip: receivedUser.zip,
                firstName: receivedUser.firstName,
                lastName: receivedUser.lastName,
                accessLevel: receivedUser.accessLevel,
                clientId: client.id
              });

              setIsSignedIn(true);
              questionFormik.handleSubmit();

            } else
              toast.error(t("There is something wrong with email update"));

          });
        } else
          toast.error(t("Please fill in Email, Zip Code and First Name"));
      }
    }

  });

  const { errors, touched, setFieldValue, values, handleBlur, resetForm } = questionFormik;
  const { values: userValues, isValid: isUserDataValid, setTouched: setTouchedUserData} = userDataFormik;

  const promptUserToEnterData = () => {
    setOnNewUserFlow(true);
    setOnPromptToEnterData(true);
  }

  return (
    <div className={classes.postNewQuestion + ' PostNewQuestion'}>
      <FormikProvider value={questionFormik}>
        <Form>
          <h5>
            <strong>{t("What's your question?")}</strong>
          </h5>
          <div className={classes.inputWithLabel}>
            <label htmlFor="question">
              {t("Your Question")} <span className={classes.counter}> {250 - questionCharacterCount} {t("characters left")}</span>
            </label>

            <textarea rows={4} id="question" name="question" value={values.question} maxLength={250}
              className={errors.question && touched.question ? classes.hasError : ''}
              onBlur={handleBlur}
              onChange={e => {
                setFieldValue('question', e.target.value);
                setQuestionCharacterCount(e.target.value.length);
              }}
            ></textarea>

            {errors.question && touched.question ? <div className={classes.errorText}>{errors.question}</div> : null}
          </div>

          <QuestionRecommendations
            newQuestionText={values.question}
            handleClose={handleClose}
            promptUserToEnterData={promptUserToEnterData}
            userData={{email: userValues.email, firstName: userValues.firstName, zip: userValues.zip, isUserDataValid: isUserDataValid}}
          />

          <div className={classes.inputWithLabel}>
            <div className={classes.selectArrow}>
              <Icons iconType="chevron-down" iconClass={classes.downChevron} />
            </div>

            <label htmlFor="category">{t("Category")}</label>

            <div className={classes.categorySelect}>
              <label className={classes.inset} htmlFor="category">
                <Icons iconType={values.category} iconClass={classes.scroll} />
              </label>

              <select name="" id="category" className={classes.outset}
                onBlur={handleBlur} onChange={e => { setFieldValue('category', e.target.value); }}
              >
                <option value=""></option>
                {categories.map(category => (
                  <option value={category} key={category}>
                    {translateCustomText(category, category)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Form>
      </FormikProvider>

      <FormikProvider value={userDataFormik}>
        <Form onChange={() => setOnPromptToEnterData(false)}>
          <div className={classes.userData}>
            <div className={classes.inputWithLabel}>
              <label>
                {t("Email")}
              </label>

              <Field
                type="text"
                name="email"
                disabled={isSignedIn}
                className={onPromptToEnterData ? classes.prompt : (userDataFormik.touched.email && userDataFormik.errors.email && classes.hasError)}
              />

              {userDataFormik.errors.email && userDataFormik.touched.email &&
              <div className={classes.errorText}>{userDataFormik.errors.email}</div>
              }
            </div>

            {onNewUserFlow && <>
              <div className={classes.inputWithLabel}>
                <label htmlFor="zip">
                  {t("Zip Code")}<small> ( {t('required')} )</small>
                </label>

                <Field
                  type="text"
                  name="zip"
                  className={onPromptToEnterData ? classes.prompt : (userDataFormik.errors.zip && userDataFormik.touched.zip && classes.hasError)}
                />

                {userDataFormik.errors.zip && userDataFormik.touched.zip &&
                  <div className={classes.errorText}>{userDataFormik.errors.zip}</div>
                }
              </div>

              <div className={classes.inputWithLabel}>
                <label htmlFor="firstName">
                  {t("First Name")}<small> ( {t("Displayed next to your question")} )</small>
                </label>

                <Field
                  type="text"
                  name="firstName"
                  className={onPromptToEnterData ? classes.prompt : (userDataFormik.errors.firstName && userDataFormik.touched.firstName && classes.hasError)}
                />

                {userDataFormik.errors.firstName && userDataFormik.touched.firstName &&
                  <div className={classes.errorText}>{userDataFormik.errors.firstName}</div>
                }
              </div>

              <div className={classes.inputWithLabel}>
                <label htmlFor="lastName">
                  {t("Last Name")}<small> ( {t("This will not be displayed to other users")} )</small>
                </label>

                <Field
                  type="text"
                  name="lastName"
                />
              </div>
            </>}
          </div>
        </Form>
      </FormikProvider>

      <div className={classes.buttonGroup}>
        <Button
          callback={() => { handleClose(); resetForm(); }}
          customClass={classes.cancel + ' cancel'}
          text={t("Cancel")}
        />

        <Button
          customClass={classes.primary + ' primary'}
          callback={() => { userDataFormik.handleSubmit(); }}
          text="Ok"
        />
      </div>

      {showCompletionModule &&
        <CompletionModal
          handleClose={ () => { setShowCompletionModule(false); handleClose(); } }
          magicLinkSent={magicLinkSent}
        />
      }

      <ToastContainer position="top-center" />
    </div>
  );
}
